import { useEffect, useState } from "react";
import { api } from "./api";

const IMAGE_EXTENSIONS = {
  "image/jpeg": ".jpg",
  "image/jpg": ".jpg",
  "image/pjpeg": ".jpg",
  "image/png": ".png",
  "image/webp": ".webp",
  "image/heic": ".heic",
  "image/heif": ".heif",
  "image/heic-sequence": ".heic",
  "image/heif-sequence": ".heif",
};
const RESIZABLE_IMAGE_TYPES = new Set(["image/jpeg", "image/jpg", "image/pjpeg", "image/png", "image/webp"]);
const MAX_UPLOAD_DIMENSION = 2200;
const JPEG_QUALITY = 0.82;

export function useFileUrl(path) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cleanup = null;
    let cancelled = false;
    if (!path) {
      setUrl(null);
      return;
    }
    (async () => {
      try {
        const resp = await api.get(`/files/${encodeURIComponent(path)}`, { responseType: "blob" });
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(resp.data);
        setUrl(blobUrl);
        cleanup = () => URL.revokeObjectURL(blobUrl);
      } catch (err) {
        if (err?.response?.status && err.response.status !== 401) {
          console.error(`useFileUrl failed for path=${path}:`, err);
        }
        setUrl(null);
      }
    })();
    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, [path]);
  return url;
}

function uploadFilename(file, fallbackType) {
  const type = (file.type || fallbackType || "").split(";", 1)[0].toLowerCase();
  const ext = IMAGE_EXTENSIONS[type] || ".bin";
  const name = (file.name || "").trim();
  const suffix = (name.match(/\.[a-z0-9]{2,5}$/i)?.[0] || "").toLowerCase();
  const allowed = new Set([ext]);
  if (["image/jpeg", "image/jpg", "image/pjpeg"].includes(type)) [".jpg", ".jpeg"].forEach((v) => allowed.add(v));
  if (["image/heic", "image/heic-sequence"].includes(type)) allowed.add(".heic");
  if (["image/heif", "image/heif-sequence"].includes(type)) allowed.add(".heif");
  if (name && allowed.has(suffix)) return name;
  const stem = name.replace(/\.[a-z0-9]{2,5}$/i, "").replace(/[^a-z0-9_-]+/gi, "-").replace(/^-+|-+$/g, "") || "camera-photo";
  return `${stem}${ext}`;
}

function loadImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Browser could not read the selected camera image for resizing."));
    };
    img.src = url;
  });
}

async function resizeImageIfNeeded(file) {
  const type = (file.type || "").split(";", 1)[0].toLowerCase();
  if (!RESIZABLE_IMAGE_TYPES.has(type) || typeof document === "undefined") return file;

  const img = await loadImage(file);
  const largestSide = Math.max(img.width, img.height);
  if (largestSide <= MAX_UPLOAD_DIMENSION && file.size <= 8 * 1024 * 1024) return file;

  const scale = Math.min(1, MAX_UPLOAD_DIMENSION / largestSide);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(img.width * scale));
  canvas.height = Math.max(1, Math.round(img.height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const outputType = type === "image/png" ? "image/png" : "image/jpeg";
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, outputType, JPEG_QUALITY));
  if (!blob || blob.size >= file.size) return file;

  const nextName = uploadFilename(file, outputType).replace(/\.[a-z0-9]{2,5}$/i, outputType === "image/png" ? ".png" : ".jpg");
  return new File([blob], nextName, { type: outputType, lastModified: file.lastModified || Date.now() });
}

export async function prepareUploadFile(file) {
  try {
    return await resizeImageIfNeeded(file);
  } catch (err) {
    console.warn("Image resize skipped; uploading original file", {
      name: file?.name,
      type: file?.type,
      size: file?.size,
      reason: err?.message || String(err),
    });
    return file;
  }
}

export async function uploadFile(file) {
  const prepared = await prepareUploadFile(file);
  const fd = new FormData();
  fd.append("file", prepared, uploadFilename(prepared, file?.type));
  try {
    const { data } = await api.post("/upload", fd);
    return data; // { id, path, size }
  } catch (err) {
    console.error("Upload failed", {
      original: { name: file?.name, type: file?.type, size: file?.size },
      prepared: { name: prepared?.name, type: prepared?.type, size: prepared?.size },
      status: err?.response?.status,
      detail: err?.response?.data?.detail,
    });
    throw err;
  }
}
